import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../../../providers/userContextProvider';
import { Account } from '../../../interfaces';
import { usePageState } from '../../../util/usePageState';
import {
  Typography, Skeleton,
} from '../../1-primative';
import {
  Table, TableRow, TableCell, TableBody, Pagination,
  Card, TableHeadCell,
  Badge,
} from '../../2-component';
import { entityName } from '../../../util';

const FETCH_ACCOUNTS = gql`
  query fetchAccounts($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      accounts {
        id
        custodianAccountNumber
        user {
          id
          firstName
          lastName
          entityName
        }
        type
        organization {
          id
          name
        }
      }
      totalCount
    }
  }
`;

export const Accounts = () => {
  const { activeOrganization } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation(['accountsDetail', 'accountTypes']);
  const [page, setPage] = usePageState(1, 'page');
  const pageSize = 15;

  const {
    loading, error, data, previousData,
  } = useQuery(FETCH_ACCOUNTS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
        },
        pagination: {
          offSet: (page - 1) * pageSize,
          perPage: pageSize,
        },
      },
    },
  });

  if (error) return (<Typography>Error</Typography>);

  return (
    <Card loading={loading}>
      <Table sx={{ minWidth: 650 }} aria-label="accounts table">
        <TableBody>
          <TableRow>
            <TableHeadCell>{t('table.userName')}</TableHeadCell>
            <TableHeadCell>{t('table.accountType')}</TableHeadCell>
            <TableHeadCell>{t('table.accountNumber')}</TableHeadCell>
            <TableHeadCell>{t('table.organization')}</TableHeadCell>
          </TableRow>
          {loading && !previousData && [...Array(15)].map((x: any, i: number) => (
            <TableRow key={i}>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
            </TableRow>
          ))}
          {(data || previousData)?.fetchAccounts?.accounts?.map((account: Account) => (
            <TableRow
              hover
              pointer
              onClick={() => {
                navigate(`/clients/${account.user.id}/account/${account.id}`);
              }}
              key={account.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell>
                {entityName(account.user)}
              </TableCell>
              <TableCell>
                {t(`accountTypes:${account.type}`)}
              </TableCell>
              <TableCell component="th" scope="row">
                {account.custodianAccountNumber ? <Badge label={account.custodianAccountNumber} /> : '-'}
              </TableCell>
              <TableCell>
                {account.organization?.name}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        count={Math.ceil(((data || previousData)?.fetchAccounts?.totalCount ?? 0) / pageSize)}
        page={page}
        onChange={(_e, newPage) => setPage(newPage)}
        sx={{
          p: 1,
          textAlign: 'right',
          '.MuiPagination-ul': {
            justifyContent: 'end',
          },
        }}
      />
    </Card>
  );
};

export default Accounts;
